<template>
  <main :data-wio-id="pageId">
    <div v-if="page" class="home">
      <section class="hero">
        <RichImage :field="page.hero" class="hero-image" />
        <div class="content">
          <div class="hero-column hero-column-large">
            <RichText :field="page.title" class="title marked" />
            <RichText :field="page.subtitle" class="subtitle sub-header" />
          </div>
          <div class="hero-column">
            <router-link
              v-for="button in callToAction.items"
              :to="button.button_link || button.btn_target"
              :key="`cta-${button.button_label || button.button_label[0].text}`"
            >
              <Button :icon="button.icon" class="btn-primary">
                {{ button.button_label[0].text || button.button_label }}
              </Button>
            </router-link>
          </div>
        </div>
      </section>
      <Products v-if="products" :products="products.items" />
      <Article
        v-for="(article, index) in articles.items"
        :key="index"
        :article="article"
      />
      <CallToAction v-if="callToAction" :article="callToAction" />
    </div>
    <div v-else class="content-min-height">
      <Spinner />
    </div>
  </main>
</template>
<script>
import { mapState } from 'vuex';
import Products from '@/components/Products';
import Article from '@/components/Article';
import CallToAction from '@/components/CallToAction';
import Button from '@/components/Button';
import Spinner from '@/components/Spinner';
import Icon from '@/components/Icon';

export default {
  name: 'HomePage',
  components: {
    Products,
    Article,
    CallToAction,
    Button,
    Spinner,
    Icon
  },
  computed: {
    ...mapState(['homePage']),
    page() {
      if (!this.homePage) {
        return;
      }
      return this.homePage.data;
    },
    pageId() {
      if (!this.homePage) {
        return;
      }
      return this.homePage.id;
    },
    groups() {
      if (!this.page) {
        return [];
      }
      return this.page.buttons
        .map(button => button.group)
        .reduce((groups, group) => {
          if (!groups.includes(group)) {
            groups.push(group);
          }
          return groups;
        }, []);
    },
    products() {
      if (!this.page) {
        return [];
      }
      return this.page.body.find(i => i.slice_type === 'products');
    },
    articles() {
      if (!this.page) {
        return null;
      }
      return this.page.body.find(i => i.slice_type === 'grouped_articles');
    },
    callToAction() {
      if (!this.page) {
        return null;
      }
      return this.page.body.find(i => i.slice_type === 'call_to_action');
    }
  },
  watch: {
    pageId() {
      if (window && window.PrismicToolbar) {
        window.PrismicToolbar.setupEditButton();
      }
    },
    page(newHomePage, oldHomePage) {
      // Refetch page when language changed
      if (oldHomePage && !newHomePage) {
        this.$store.dispatch('getHomePage');
      }
    }
  },
  async serverPrefetch() {
    await this.$store.dispatch('getHomePage');
  },

  mounted() {
    if (!this.homePage.length) {
      this.$store.dispatch('getHomePage');
    }
    if (window && window.PrismicToolbar) {
      window.PrismicToolbar.setupEditButton();
    }
  },
  methods: {
    groupedButtons(group) {
      if (!this.page) {
        return [];
      }
      return this.page.buttons.filter(button => button.group === group);
    }
  }
};
</script>
<style lang="scss" scoped>
@import '../scss/base/colors';
@import '../scss/helpers/mixins';

.hero {
  display: flex;
  flex-direction: column;
  align-items: center;

  border-bottom: 1px $grey-3 solid;
  margin-bottom: 30px;

  .content {
    display: flex;
    max-width: 1000px;
    padding: 20px 20px 10px;
    flex-direction: row;
    @include breakpoint-max(XS) {
      flex-direction: column;
    }
  }

  .hero-column {
    flex: 1;
  }

  .hero-column-large {
    flex-basis: 75%;
    max-width: 75%;
    padding-right: 3em;
    [dir='rtl'] & {
      padding-right: 0;
      padding-left: 3em;
    }
    @include breakpoint-max(XS) {
      padding: 0;
      flex-basis: 100%;
      max-width: 100%;
    }
  }

  .hero-image {
    min-height: 260px;
    max-width: 100vw;
    object-fit: cover;
  }

  @include breakpoint(M) {
    margin-bottom: 80px;

    .content {
      padding-top: 70px;
      padding-bottom: 30px;

      [dir='rtl'] & {
        padding-right: 20px;
      }
    }
  }
}
</style>
